import { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { Button } from '../components/common/Button';
import './Admin.css';

const Admin = () => {
  const [view, setView] = useState('upload'); // upload, episodes, words, reported
  const [jsonFile, setJsonFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(null);

  // Browsing State
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Stats/Words State
  const [wordsStats, setWordsStats] = useState([]);
  const [totalWords, setTotalWords] = useState(0);
  const [lastWordSnapshot, setLastWordSnapshot] = useState(null);
  const [reportedWords, setReportedWords] = useState([]);

  // Logic to load all data initially (lightweight enough for admin)
  const loadData = async () => {
    setLoading(true);
    const eps = await adminService.getEpisodes();
    setAllEpisodes(eps);

    // Extract seasons
    const uniqueSeasons = [...new Set(eps.map((e) => e.season))].sort().reverse();
    setSeasons(uniqueSeasons);
    setLoading(false);
  };

  const loadWordsFreq = async () => {
    setLoading(true);
    // Initial load
    const { words: w, lastDoc } = await adminService.getWordsByFrequency(50);
    const count = await adminService.getTotalWordCount();

    setWordsStats(w);
    setLastWordSnapshot(lastDoc);
    setTotalWords(count);
    setLoading(false);
  };

  const loadReportedWords = async () => {
    setLoading(true);
    try {
      const words = await adminService.getReportedWords();
      setReportedWords(words);
    } catch (e) {
      console.error('Error loading reported words', e);
      alert('Errore nel caricamento parole segnalate');
    }
    setLoading(false);
  };

  const handleLoadMoreWords = async () => {
    if (!lastWordSnapshot) return;
    setLoading(true);
    try {
      const { words: newWords, lastDoc } = await adminService.getWordsByFrequency(
        50,
        lastWordSnapshot,
      );
      setWordsStats((prev) => [...prev, ...newWords]);
      setLastWordSnapshot(lastDoc);
    } catch (e) {
      console.error('Error loading more words', e);
      alert('Errore nel caricamento altre parole');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (view === 'episodes') loadData();
    if (view === 'words') loadWordsFreq();
    if (view === 'reported') loadReportedWords();
  }, [view]);

  // Handlers
  const handleUpload = async () => {
    if (!jsonFile) return;
    setLoading(true);
    setUploadStatus('Caricamento in corso...');
    try {
      const text = await jsonFile.text();
      const data = JSON.parse(text);
      const result = await adminService.uploadDataset(data);
      setUploadStatus(
        `Successo! Caricate ${result.episodesCount} nuove puntate e aggiornate ${result.wordsCount} parole.`,
      );
      // Reload if in view
      if (view === 'episodes') loadData();
    } catch (e) {
      console.error(e);
      setUploadStatus('Errore: ' + e.message);
    }
    setLoading(false);
  };

  // Episode Editor Handlers
  const handleAddWord = async () => {
    const newWord = prompt('Inserisci nuova parola:');
    if (!newWord) return;

    const updatedWords = [...(selectedEpisode.words || []), newWord.toUpperCase()];
    const updatedEp = { ...selectedEpisode, words: updatedWords };

    setSelectedEpisode(updatedEp); // Optimistic UI
    await adminService.updateEpisode(selectedEpisode.id, { words: updatedWords });
  };

  const handleRemoveWord = async (index) => {
    if (!confirm('Eliminare parola?')) return;
    const updatedWords = selectedEpisode.words.filter((_, i) => i !== index);
    const updatedEp = { ...selectedEpisode, words: updatedWords };

    setSelectedEpisode(updatedEp);
    await adminService.updateEpisode(selectedEpisode.id, { words: updatedWords });
  };

  const handleEditWord = async (index) => {
    const oldWord = selectedEpisode.words[index];
    const newWord = prompt('Modifica parola:', oldWord);
    if (!newWord || newWord === oldWord) return;

    const updatedWords = [...selectedEpisode.words];
    updatedWords[index] = newWord.toUpperCase();

    const updatedEp = { ...selectedEpisode, words: updatedWords };

    setSelectedEpisode(updatedEp);
    await adminService.updateEpisode(selectedEpisode.id, { words: updatedWords });
  };

  const handleMigrateRandom = async () => {
    if (
      !confirm(
        'Vuoi inizializzare il campo casuale per tutte le parole? Questo migliorerà la randomicità del gioco.',
      )
    )
      return;
    setLoading(true);
    setMigrationProgress({ current: 0, total: 0 });
    try {
      const res = await adminService.migrateRandomField((current, total) => {
        setMigrationProgress({ current, total });
      });
      alert(`Migrazione completata! ${res.total} parole aggiornate.`);
    } catch (e) {
      console.error(e);
      alert('Errore durante la migrazione: ' + e.message);
    }
    setLoading(false);
    setMigrationProgress(null);
  };

  const handleInitializeStats = async () => {
    if (
      !confirm(
        'Vuoi inizializzare i campi statistici (stato, corrette, errate, ecc.) per tutte le parole? I valori esistenti verranno sovrascritti dai default.',
      )
    )
      return;
    setLoading(true);
    setMigrationProgress({ current: 0, total: 0 });
    try {
      const res = await adminService.initializeAllWordStats((current, total) => {
        setMigrationProgress({ current, total });
      });
      alert(`Inizializzazione completata! ${res.total} parole aggiornate.`);
    } catch (e) {
      console.error(e);
      alert("Errore durante l'inizializzazione: " + e.message);
    }
    setLoading(false);
    setMigrationProgress(null);
  };

  const handleConfirmWord = async (wordId) => {
    try {
      await adminService.updateWordStatus(wordId, 'CONFERMATO');
      setReportedWords((prev) => prev.filter((w) => w.id !== wordId));
    } catch (e) {
      console.error(e);
      alert('Errore durante la conferma');
    }
  };

  const handleDeleteWordFromDb = async (wordId) => {
    if (!confirm(`Sei sicuro di voler eliminare la parola "${wordId}" dal database?`)) return;
    try {
      await adminService.deleteWord(wordId);
      setReportedWords((prev) => prev.filter((w) => w.id !== wordId));
    } catch (e) {
      console.error(e);
      alert("Errore durante l'eliminazione");
    }
  };

  // Render Helpers
  const renderEpisodesView = () => {
    // Level 3: Episode Detail
    if (selectedEpisode) {
      return (
        <div className="detail-view">
          <Button variant="ghost" onClick={() => setSelectedEpisode(null)}>
            ← Torna a {selectedSeason}
          </Button>

          <div className="detail-header">
            <h2>{selectedEpisode.date}</h2>
            <p>{selectedEpisode.title}</p>
            <a
              href={selectedEpisode.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rai-link"
            >
              Guarda su RaiPlay ↗
            </a>
          </div>

          <div className="words-editor">
            <h3>Parole ({selectedEpisode.words?.length || 0})</h3>
            <div className="words-grid">
              {selectedEpisode.words?.map((w, i) => (
                <div key={w} className="word-chip">
                  <span>
                    {i + 1}. {w}
                  </span>
                  <div className="word-actions">
                    <button onClick={() => handleEditWord(i)}>✏️</button>
                    <button onClick={() => handleRemoveWord(i)}>❌</button>
                  </div>
                </div>
              ))}
              <button className="add-word-btn" onClick={handleAddWord}>
                + Aggiungi Parola
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Level 2: Episode List for Season
    if (selectedSeason) {
      const seasonEps = allEpisodes.filter((e) => e.season === selectedSeason);
      return (
        <div className="season-view">
          <Button variant="ghost" onClick={() => setSelectedSeason(null)}>
            ← Torna alle Stagioni
          </Button>
          <h2>
            Stagione {selectedSeason} ({seasonEps.length} puntate)
          </h2>

          <div className="episodes-grid">
            {seasonEps.map((ep) => (
              <div key={ep.id} className="episode-card" onClick={() => setSelectedEpisode(ep)}>
                <h3>{ep.date}</h3>
                <small>
                  {ep.title
                    .replace('Reazione a catena - ', '')
                    .replace('Reazione a catena weekend - ', '')}
                </small>
                <div className="badge">{ep.words?.length} parole</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Level 1: Seasons List
    return (
      <div className="seasons-list">
        <h2>Seleziona Stagione</h2>
        <div className="seasons-grid">
          {seasons.map((s) => (
            <div key={s} className="season-card" onClick={() => setSelectedSeason(s)}>
              <h3>{s}</h3>
              <p>{allEpisodes.filter((e) => e.season === s).length} Puntate</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReportedView = () => {
    return (
      <div className="reported-section">
        <h2>Parole Segnalate ({reportedWords.length})</h2>
        {reportedWords.length === 0 ? (
          <p className="empty-msg">Nessuna parola segnalata al momento.</p>
        ) : (
          <div className="reported-list">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Parola</th>
                  <th>Frequenza</th>
                  <th>Stats (C/P/E)</th>
                  <th>Data Segnalazione</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {reportedWords.map((w) => (
                  <tr key={w.id}>
                    <td className="word-text">{w.text}</td>
                    <td>{w.frequency || 0}</td>
                    <td>
                      {w.corrette || 0}/{w.passate || 0}/{w.errate || 0}
                    </td>
                    <td>
                      {w.reportedAt
                        ? new Date(w.reportedAt.seconds * 1000).toLocaleString()
                        : 'N/A'}
                    </td>
                    <td className="actions-cell">
                      <Button size="sm" variant="primary" onClick={() => handleConfirmWord(w.id)}>
                        Conferma
                      </Button>
                      <Button
                        size="sm"
                        variant="error"
                        onClick={() => handleDeleteWordFromDb(w.id)}
                      >
                        Elimina
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>Admin Console</h1>
        <nav>
          <button onClick={() => setView('upload')} className={view === 'upload' ? 'active' : ''}>
            Upload
          </button>
          <button
            onClick={() => {
              setView('episodes');
              setSelectedSeason(null);
              setSelectedEpisode(null);
            }}
            className={view === 'episodes' ? 'active' : ''}
          >
            Puntate
          </button>
          <button onClick={() => setView('words')} className={view === 'words' ? 'active' : ''}>
            Parole (Freq)
          </button>
          <button
            onClick={() => setView('reported')}
            className={view === 'reported' ? 'active' : ''}
          >
            Segnalate
          </button>
        </nav>
      </header>

      <main className="admin-content">
        {view === 'upload' && (
          <div className="upload-section">
            <h2>Carica Dataset JSON</h2>
            <input type="file" accept=".json" onChange={(e) => setJsonFile(e.target.files[0])} />
            <Button onClick={handleUpload} disabled={loading || !jsonFile}>
              {loading ? 'Elaborazione...' : 'Carica JSON'}
            </Button>
            {uploadStatus ? <p className="status-msg">{uploadStatus}</p> : null}

            <div className="info-box">
              <h3>Formato atteso (esempio):</h3>
              <pre>
                {`[
  {
    "season": 1,
    "episode": 1,
    "words": ["Parola1", "Parola2"]
  }
]`}
              </pre>
            </div>

            {/* Maintenance Section */}
            <div
              style={{
                marginTop: '3rem',
                borderTop: '2px solid #ccc',
                padding: '1rem',
                background: '#f9f9f9',
                borderRadius: '8px',
              }}
            >
              <h3>Manutenzione</h3>
              <p>
                Inizializza i valori casuali per tutte le parole esistenti per migliorare la varietà
                durante il gioco.
              </p>
              <Button
                onClick={handleMigrateRandom}
                disabled={loading}
                style={{ marginBottom: '1rem' }}
              >
                {migrationProgress && migrationProgress.total > 0
                  ? `Migrazione Random... ${migrationProgress.current}/${migrationProgress.total}`
                  : 'Inizializza Randomicità Parole'}
              </Button>

              <p>Inizializza stato e contatori (corrette, errate, tempo) per tutte le parole.</p>
              <Button onClick={handleInitializeStats} disabled={loading} variant="secondary">
                {migrationProgress && migrationProgress.total > 0
                  ? `Inizializzazione Stats... ${migrationProgress.current}/${migrationProgress.total}`
                  : 'Inizializza Statistiche Parole'}
              </Button>
            </div>

            {/* Danger Zone */}
            <div
              style={{
                marginTop: '3rem',
                borderTop: '2px solid red',
                padding: '1rem',
                background: '#ffe6e6',
                borderRadius: '8px',
              }}
            >
              <h3 style={{ color: 'red' }}>Zona Pericolosa</h3>
              <p>
                Se le parole non si sono caricate (perché avevi le regole bloccate), resetta tutto e
                ricarica il JSON.
              </p>
              <Button
                variant="error"
                onClick={async () => {
                  if (
                    confirm('SEI SICURO? Cancellerà TUTTE le puntate e le parole dal database!')
                  ) {
                    setLoading(true);
                    try {
                      const res = await adminService.resetDatabase();
                      alert(
                        `Reset completato! Cancellati: ${res.episodesDeleted} puntate, ${res.wordsDeleted} parole.`,
                      );
                      // Refresh views
                      setAllEpisodes([]);
                      setWordsStats([]);
                    } catch (e) {
                      alert('Errore reset: ' + e.message);
                    }
                    setLoading(false);
                  }
                }}
              >
                RESETTA DATABASE
              </Button>
            </div>
          </div>
        )}

        {view === 'episodes' && renderEpisodesView()}

        {view === 'reported' && renderReportedView()}

        {view === 'words' && (
          <div className="list-section">
            <h2>Parole più frequenti (Totale: {totalWords})</h2>
            <div className="words-list">
              {wordsStats.map((w, i) => (
                <div key={w.id} className="word-row">
                  <span className="rank">#{i + 1}</span>
                  <span className="word">{w.text}</span>
                  <span className="count">{w.frequency} volte</span>
                </div>
              ))}
            </div>
            {lastWordSnapshot ? (
              <Button size="sm" onClick={handleLoadMoreWords} disabled={loading}>
                {loading ? 'Caricamento...' : 'Carica altre 50'}
              </Button>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
